import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './Lobby.css';
import { LOBBY_DOODLE_PAD, AI_PLAYERS } from '../featureFlags';
import Mascot, { ALL_MASCOT_IDS } from './Mascot';
import PlayerAvatar from './PlayerAvatar';

const AVATARS = ALL_MASCOT_IDS;

const DRAWING_TIMER_OPTIONS = [
  { value: 30, label: '30s' },
  { value: 60, label: '60s' },
  { value: 90, label: '90s' },
  { value: 120, label: '120s' },
  { value: 0, label: '∞' },
];

const GUESS_TIMER_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 30, label: '30s' },
  { value: 45, label: '45s' },
  { value: 60, label: '60s' },
];

const CHAIN_LENGTH_OPTIONS = [
  { value: 'short', label: 'Short' },
  { value: 'normal', label: 'Normal' },
  { value: 'long', label: 'Long' },
];

const DOODLE_COLORS = [
  '#3d2b1f', '#c4622d', '#e8a020', '#4a9e4a',
  '#2a7ab5', '#8b4db5', '#c42d5e', '#888888', '#ffffff',
];
const DOODLE_SIZES = [3, 7];
const CANVAS_W = 600;
const CANVAS_H = 380;

function drawStrokeOnCtx(ctx, stroke) {
  const pts = stroke.points;
  if (!pts || pts.length === 0) return;
  ctx.beginPath();
  ctx.strokeStyle = stroke.color;
  ctx.lineWidth = stroke.size;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (pts.length === 1) {
    ctx.arc(pts[0].x, pts[0].y, stroke.size / 2, 0, Math.PI * 2);
    ctx.fillStyle = stroke.color;
    ctx.fill();
  } else {
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.stroke();
  }
}

function LobbyDoodlePad({ strokes, onDoodleStroke, playerName, playerAvatar }) {
  const canvasRef = useRef(null);
  const [color, setColor] = useState('#c4622d');
  const [size, setSize] = useState(DOODLE_SIZES[0]);
  const isDrawingRef = useRef(false);
  const currentPointsRef = useRef([]);
  const strokesRef = useRef(strokes);
  strokesRef.current = strokes;
  const colorRef = useRef(color);
  colorRef.current = color;
  const sizeRef = useRef(size);
  sizeRef.current = size;

  const redraw = useCallback((currentPoints = null) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fffef9';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    for (const s of strokesRef.current) drawStrokeOnCtx(ctx, s);
    if (currentPoints && currentPoints.length > 0) {
      drawStrokeOnCtx(ctx, { points: currentPoints, color: colorRef.current, size: sizeRef.current });
    }
  }, []);

  useEffect(() => { redraw(); }, [strokes, redraw]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: Math.round((src.clientX - rect.left) * scaleX * 10) / 10,
      y: Math.round((src.clientY - rect.top) * scaleY * 10) / 10,
    };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    isDrawingRef.current = true;
    const pos = getPos(e);
    if (pos) currentPointsRef.current = [pos];
  };

  const handlePointerMove = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const pos = getPos(e);
    if (pos) {
      currentPointsRef.current.push(pos);
      redraw(currentPointsRef.current);
    }
  };

  const handlePointerUp = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const points = [...currentPointsRef.current];
    currentPointsRef.current = [];
    if (points.length > 0) {
      onDoodleStroke({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        color: colorRef.current,
        size: sizeRef.current,
        points,
        playerName,
        playerAvatar,
      });
    }
  };

  return (
    <div className="doodle-pad-section">
      <h2 className="doodle-section-title"><span className="material-symbols-outlined" style={{fontSize: '16px', verticalAlign: 'middle', marginRight: '4px'}}>draw</span> Warm-up Doodle</h2>
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        className="doodle-pad-canvas"
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
      />
      <div className="doodle-toolbar">
        <div className="doodle-colors">
          {DOODLE_COLORS.map(c => (
            <button
              key={c}
              type="button"
              aria-label={`color ${c}`}
              className={`doodle-color-btn ${color === c ? 'active' : ''}`}
              style={{ '--doodle-color': c }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>
        <div className="doodle-sizes">
          {DOODLE_SIZES.map(s => (
            <button
              key={s}
              type="button"
              className={`doodle-size-btn ${size === s ? 'active' : ''}`}
              onClick={() => setSize(s)}
            >
              <span
                className="doodle-size-dot"
                style={{ width: s * 2.5, height: s * 2.5, background: color }}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function settingsSummary(settings) {
  const drawLabel = settings.drawing_timer === 0 ? '∞ drawing' : `${settings.drawing_timer}s drawing`;
  const guessLabel = settings.guess_timer === 0 ? 'no guess timer' : `${settings.guess_timer}s guessing`;
  const chainLabel = settings.chain_length === 'short' ? 'Short chains' : settings.chain_length === 'long' ? 'Long chains' : 'Normal chains';
  return `${drawLabel} · ${guessLabel} · ${chainLabel}`;
}

function ArrowStepper({ options, value, onChange }) {
  const idx = options.findIndex(o => String(o.value) === String(value));
  const current = options[idx] ?? options[0];
  const prev = () => { if (idx > 0) onChange(options[idx - 1].value); };
  const next = () => { if (idx < options.length - 1) onChange(options[idx + 1].value); };
  return (
    <div className="settings-stepper">
      <button type="button" className="settings-stepper-btn" onClick={prev} disabled={idx === 0}>‹</button>
      <span className="settings-stepper-value">{current.label}</span>
      <button type="button" className="settings-stepper-btn" onClick={next} disabled={idx === options.length - 1}>›</button>
    </div>
  );
}

function Lobby({ roomCode, playerName, players, playerId, isHostOverride, onStartGame, onChangeAvatar, onAddAiPlayer, onRemoveAiPlayer, onKickPlayer, settings, onUpdateSettings, lobbyStrokes, onDoodleStroke }) {
  const [codeCopied, setCodeCopied] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [wavingId, setWavingId] = useState(null);
  const pickerRef = useRef(null);
  const prevPlayerIdsRef = useRef(new Set(players.map(p => p.id)));
  const isHost = isHostOverride ?? false;

  // Detect new player joins → trigger wave animation on all existing avatars
  useEffect(() => {
    const prevIds = prevPlayerIdsRef.current;
    const newPlayer = players.find(p => !prevIds.has(p.id));
    prevPlayerIdsRef.current = new Set(players.map(p => p.id));
    if (newPlayer && prevIds.size > 0) {
      setWavingId(newPlayer.id);
      const timer = setTimeout(() => setWavingId(null), 1200);
      return () => clearTimeout(timer);
    }
  }, [players]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowAvatarPicker(false);
      }
    };
    if (showAvatarPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAvatarPicker]);

  const getInviteLink = () => {
    const url = new URL(window.location.origin);
    url.searchParams.set('join', roomCode);
    return url.toString();
  };

  const copyRoomCode = () => {
    navigator.clipboard.writeText(roomCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const [urlCopied, setUrlCopied] = useState(false);
  const copyInviteUrl = () => {
    navigator.clipboard.writeText(getInviteLink());
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  const handleShare = async () => {
    const link = getInviteLink();
    const shareData = {
      title: 'Join my Pictaphone game!',
      text: `Join my Pictaphone game! Room code: ${roomCode} 🎨`,
      url: link,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(`Join my Pictaphone game! Room code: ${roomCode} 🎨 ${link}`);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const updateSetting = (key, value) => {
    onUpdateSettings({ [key]: value });
  };

  const s = settings || {};

  return (
    <div className="lobby-container">
      <div className="lobby-card">
        <div className="lobby-pictaphone-bar">
          <span className="lobby-pictaphone-wordmark">PICTAPHONE</span>
        </div>
        <div className="lobby-header">
          <div className="room-card">
            <div className="room-card-left">
              <span className="room-card-caption">Room code</span>
              <button
                className={`room-code-display ${codeCopied ? 'copied' : ''}`}
                onClick={copyRoomCode}
                title="Tap to copy room code"
              >
                {roomCode}
                {codeCopied && <span className="room-code-hint">copied!</span>}
              </button>
              <button className="room-share-btn" onClick={handleShare}>
                <span className="material-symbols-outlined" aria-hidden="true">share</span>
                <span>Share</span>
              </button>
            </div>
            <div className="room-card-right">
              <div className="room-card-qr">
                <QRCodeSVG value={getInviteLink()} size={96} level="M" bgColor="transparent" fgColor="#5A4800" />
              </div>
              <button
                className={`room-card-qr-label ${urlCopied ? 'copied' : ''}`}
                onClick={copyInviteUrl}
                title="Copy invite link"
              >
                {urlCopied ? 'Link copied!' : 'Or, scan to join'}
              </button>
            </div>
          </div>
        </div>

        <div className="lobby-scrollable">
          <div className="players-section">
          <h2 className="players-title">
            Today's crew <span className="players-title-count">({players.length})</span>
          </h2>
          <div className="players-grid">
            {players.map((player, index) => {
              const isMe = player.id === playerId;
              // "you" lives in the status line, not appended to the name — the name
              // needs every pixel of the card at phone widths.
              const baseStatus = player.is_ai
                ? 'AI player'
                : isMe
                  ? (index === 0 ? '✦ host · you' : 'you')
                  : (index === 0 ? '✦ host' : 'ready!');
              const isOffline = !!player.disconnected;
              const canKick = isHost && !isMe && !player.is_ai && player.unresponsive && onKickPlayer;
              const status = isOffline ? 'offline' : baseStatus;
              return (
                <div
                  key={player.id}
                  className={`player-card ${isMe ? 'player-card--me' : ''}${wavingId === player.id ? ' player-card--new' : ''}${isOffline ? ' player-card--offline' : ''}`}
                >
                  <div
                    className={`player-card-avatar ${isMe ? 'clickable' : ''}${wavingId && wavingId !== player.id ? ' avatar-wave' : ''}${wavingId === player.id ? ' avatar-arriving' : ''}`}
                    style={{ '--idle-delay': `${index * 0.4}s` }}
                    onClick={isMe ? () => setShowAvatarPicker(!showAvatarPicker) : undefined}
                    title={isMe ? 'Tap to change avatar' : ''}
                  >
                    <PlayerAvatar avatar={player.avatar} fallback={player.name.charAt(0).toUpperCase()} size={44} />
                    {player.is_ai && isHost && (
                      <button
                        className="remove-ai-btn"
                        onClick={() => onRemoveAiPlayer(player.id)}
                        title="Remove AI player"
                      >✕</button>
                    )}
                    {isOffline && (
                      <span className="player-card-offline-dot" title="Disconnected">
                        <span className="material-symbols-outlined">cloud_off</span>
                      </span>
                    )}
                  </div>
                  <div className="player-card-text">
                    <div className="player-card-name" title={player.name}>
                      {player.name}
                    </div>
                    <div className="player-card-status">
                      {status}
                    </div>
                  </div>
                  {canKick && (
                    <button
                      type="button"
                      className="kick-btn player-card-kick-btn"
                      onClick={() => onKickPlayer(player.id)}
                      title={`Remove ${player.name} from the game`}
                    >
                      <span className="material-symbols-outlined kick-btn-icon">logout</span>
                      Boot
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

          {LOBBY_DOODLE_PAD && (
            <LobbyDoodlePad
              strokes={lobbyStrokes || []}
              onDoodleStroke={onDoodleStroke}
              playerName={playerName}
              playerAvatar={players.find(p => p.name === playerName)?.avatar || ''}
            />
          )}

          {/* Settings panel */}
          <div className="settings-panel">
            <h2 className="settings-title">Game Settings</h2>
            {isHost ? (
              <div className="settings-rows">
                <div className="settings-row">
                  <span className="settings-label">Drawing Time</span>
                  <ArrowStepper
                    options={DRAWING_TIMER_OPTIONS}
                    value={s.drawing_timer ?? 90}
                    onChange={v => updateSetting('drawing_timer', v)}
                  />
                </div>
                <div className="settings-row">
                  <span className="settings-label">Guessing Time</span>
                  <ArrowStepper
                    options={GUESS_TIMER_OPTIONS}
                    value={s.guess_timer ?? 45}
                    onChange={v => updateSetting('guess_timer', v)}
                  />
                </div>
                <div className="settings-row">
                  <span className="settings-label">Chain Length</span>
                  <ArrowStepper
                    options={CHAIN_LENGTH_OPTIONS}
                    value={s.chain_length ?? 'normal'}
                    onChange={v => updateSetting('chain_length', v)}
                  />
                </div>
              </div>
            ) : (
              <p className="settings-summary">{settingsSummary(s)}</p>
            )}
          </div>
        </div>

        {showAvatarPicker && (
          <div className="avatar-overlay" ref={pickerRef}>
            <div className="avatar-popup">
              <p className="avatar-popup-title">pick your face</p>
              <div className="avatar-popup-grid">
                {AVATARS.map((avatar) => {
                  const currentPlayer = players.find(p => p.id === playerId);
                  const isSelected = currentPlayer?.avatar === avatar;
                  const takenBy = players.find(p => p.avatar === avatar && p.id !== playerId);
                  const isTaken = !!takenBy;
                  return (
                    <button
                      key={avatar}
                      type="button"
                      className={`avatar-popup-pick ${isSelected ? 'selected' : ''} ${isTaken ? 'taken' : ''}`}
                      onClick={() => {
                        if (!isTaken) {
                          onChangeAvatar(avatar);
                          setShowAvatarPicker(false);
                        }
                      }}
                      disabled={isTaken}
                      title={isTaken ? `Taken by ${takenBy.name}` : ''}
                    >
                      <Mascot id={avatar} size={56} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="lobby-actions">
          {isHost ? (
            <>
              <button
                className="start-button"
                disabled={players.filter(p => !p.disconnected).length < 2}
                onClick={onStartGame}
              >
                <span className="start-button-label">
                  <span className="start-button-icon"><span className="material-symbols-outlined">play_arrow</span></span>
                  Let's Go!
                </span>
              </button>
              {AI_PLAYERS && (
                <button
                  className="add-ai-button"
                  onClick={onAddAiPlayer}
                >
                  + Add AI Player
                </button>
              )}
              {players.filter(p => !p.disconnected).length < 2 ? (
                <p className="lobby-info">
                  {AI_PLAYERS
                    ? 'Need at least 2 connected players to start. Share the room code or add an AI player!'
                    : 'Need at least 2 connected players to start. Share the room code with your friends!'}
                </p>
              ) : (
                <p className="lobby-info">
                  All set! Press Start Game when everyone is ready.
                </p>
              )}
            </>
          ) : (
            <p className="lobby-info">
              Waiting for the host to start the game...
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Lobby;
